import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import localizeDate from '../helpers/localizeDate'
import localizeStripePrice from '../helpers/localizeStripePrice'
import { Plan, StripePrice } from '../types'

interface Props {
  currency: string
  plan: Plan
  stripePriceFull: StripePrice | undefined
  stripePriceIntro: StripePrice | undefined
  trialPeriodDays?: number
}

function getIntroEndDate(interval: string, intervalCount: number): Date {
  const date = new Date()
  switch (interval) {
    case 'day':
      date.setDate(date.getDate() + intervalCount)
      break
    case 'week':
      date.setDate(date.getDate() + intervalCount * 7)
      break
    case 'month':
      date.setMonth(date.getMonth() + intervalCount)
      break
    case 'year':
      date.setFullYear(date.getFullYear() + intervalCount)
      break
    default:
      break
  }
  return date
}

export default function SubscriptionTerms({
  currency,
  plan,
  stripePriceFull,
  stripePriceIntro,
  trialPeriodDays,
}: Props) {
  const { t } = useTranslation()

  if (trialPeriodDays && stripePriceFull?.recurring) {
    const regularPrice = localizeStripePrice({
      stripePrice: stripePriceFull,
      userCurrency: currency,
    })
    const trialEndDate = localizeDate(getIntroEndDate('day', trialPeriodDays))
    const interval = stripePriceFull.recurring.interval

    return (
      <div className="space-y-4 rounded-2xl bg-black/[0.03] p-5 text-left text-sm leading-relaxed text-gray-500 sm:p-6">
        <p>{t('pages.checkout.trialPlans.subscriptionTerms.chargedToday')}</p>
        <p>
          {t('pages.checkout.trialPlans.subscriptionTerms.trialEnds', {
            date: trialEndDate,
          })}
        </p>
        <p>
          {t('pages.checkout.trialPlans.subscriptionTerms.autoRenew', {
            date: trialEndDate,
            interval: t(`pages.checkout.plans.${interval}`, { count: 1 }),
            price: regularPrice,
          })}
        </p>
        <p>
          <Trans
            components={{
              privacyLink: (
                <Link
                  className="underline"
                  target="_blank"
                  to="https://app-studio.ai/privacy"
                />
              ),
              termsLink: (
                <Link
                  className="underline"
                  target="_blank"
                  to="https://app-studio.ai/terms"
                />
              ),
            }}
            i18nKey="pages.checkout.plans.subscriptionTerms.legalBinding"
          />
        </p>
      </div>
    )
  }

  // Only relevant for recurring subscriptions that have an introductory price.
  if (
    plan === 'one-time' ||
    !stripePriceFull?.recurring ||
    !stripePriceIntro?.recurring
  ) {
    return null
  }

  const introRecurring = stripePriceIntro.recurring
  const interval = stripePriceFull.recurring.interval
  const capitalizedInterval =
    interval.charAt(0).toUpperCase() + interval.slice(1)

  const introPrice = localizeStripePrice({
    stripePrice: stripePriceIntro,
    userCurrency: currency,
  })
  const regularPrice = localizeStripePrice({
    stripePrice: stripePriceFull,
    userCurrency: currency,
  })
  const introEndDate = localizeDate(
    getIntroEndDate(introRecurring.interval, introRecurring.interval_count),
  )
  const appName = (import.meta.env.VITE_APP_NAME as string | undefined) ?? ''

  if (!introPrice || !regularPrice) return null

  return (
    <div className="space-y-4 rounded-2xl bg-black/[0.03] p-5 text-left text-sm leading-relaxed text-gray-500 sm:p-6">
      <p>
        {t('pages.checkout.plans.subscriptionTerms.chargedToday', {
          price: introPrice,
        })}
      </p>

      <p>
        {t('pages.checkout.plans.subscriptionTerms.introPeriodEnds', {
          date: introEndDate,
        })}
      </p>

      <p>
        {t('pages.checkout.plans.subscriptionTerms.cancelBeforeRenewal', {
          date: introEndDate,
        })}
      </p>

      <p>
        {t(
          `pages.checkout.plans.subscriptionTerms.autoRenew${capitalizedInterval}`,
          { appName, price: regularPrice },
        )}
      </p>

      <p>{t('pages.checkout.plans.subscriptionTerms.noPartialRefunds')}</p>

      <p>
        <Trans
          components={{
            privacyLink: (
              <Link
                className="underline"
                target="_blank"
                to="https://app-studio.ai/privacy"
              />
            ),
            termsLink: (
              <Link
                className="underline"
                target="_blank"
                to="https://app-studio.ai/terms"
              />
            ),
          }}
          i18nKey="pages.checkout.plans.subscriptionTerms.legalBinding"
        />
      </p>
    </div>
  )
}
